import {formatDate} from '@github-ui/date-picker/parser'
import {usePortalTooltip} from '@github-ui/portal-tooltip/use-portal-tooltip'
import {testIdProps} from '@github-ui/test-id-props'
import {getAnchoredPosition} from '@primer/behaviors'
import {ActionList, ActionMenu, Button, Link, Portal} from '@primer/react'
import {clsx} from 'clsx'
import {addDays, addHours, addMonths, differenceInDays, differenceInMonths, startOfMonth} from 'date-fns'
import {
  type ComponentPropsWithoutRef,
  memo,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'

import {RoadmapZoomLevel} from '../../../api/view/contracts'
import {intervalDateRange} from '../../../helpers/iterations'
import {getIterationOptionsTooltipText} from '../../../helpers/roadmap-helpers'
import {ViewerPrivileges} from '../../../helpers/viewer-privileges'
import {useEnabledFeatures} from '../../../hooks/use-enabled-features'
import {useRoadmapZoomLevel} from '../../../hooks/use-roadmap-settings'
import {
  isCurrentIterationByMarker,
  type IterationMarker,
  type MilestoneMarker,
  RoadmapMarkerType,
  useRoadmapIterationMarkersSize,
  useRoadmapMarkersInRange,
  useRoadmapMilestoneMarkersSize,
} from '../components/roadmap-markers-in-range'
import {formatDateUtc, startOfDayUtc} from '../date-utils'
import {useRoadmapHeaderHeight} from '../hooks/use-roadmap-header-height'
import {useRoadmapShowMarkersHeader} from '../hooks/use-roadmap-show-markers-header'
import {useRoadmapNavigation, useRoadmapView} from '../roadmap-view-provider'
import {getTooltipText} from '../tooltip-helper'
import styles from './roadmap-header.module.css'
import {RoadmapMarkerHeaderNubs} from './roadmap-marker'
import {RoadmapMarkerHeaderNubs as RoadmapMarkerHeaderNubsNew} from './roadmap-marker-new'
import {RoadmapTableColumnResizeProvider} from './roadmap-table-column-resize-provider'
import {RoadmapTableHeaderDragSash} from './roadmap-table-drag-sash'

const MONTH_LEFT_PADDING = 12
const MARKER_LEFT_PADDING = 12
const PAGE_PADDING = 16
const PAGE_PADDING_RIGHT = 32

export const RoadmapHeader = memo(function RoadmapHeader() {
  const {totalWidth} = useRoadmapView()
  const {memex_roadmap_focusable_marker_nubs} = useEnabledFeatures()
  const showMarkersHeader = useRoadmapShowMarkersHeader()
  const headerHeight = useRoadmapHeaderHeight()
  const {hasWritePermissions} = ViewerPrivileges()
  return (
    <div
      role="rowgroup"
      style={{
        height: headerHeight,
        width: `${totalWidth}px`,
        '--month-left-padding': `${MONTH_LEFT_PADDING}px`,
        '--marker-left-padding': `${MARKER_LEFT_PADDING}px`,
        '--page-padding': `${PAGE_PADDING}px`,
        '--page-padding-right': `${PAGE_PADDING_RIGHT}px`,
      }}
      className={styles.roadmapHeader}
      {...testIdProps('roadmap-header')}
    >
      <RoadmapMonths />
      {showMarkersHeader && <RoadmapMarkerHeaders />}
      <RoadmapDays />
      {memex_roadmap_focusable_marker_nubs ? <RoadmapMarkerHeaderNubsNew /> : <RoadmapMarkerHeaderNubs />}
      {hasWritePermissions ? (
        <div role="row" className={styles.dragHandleRow} id="end-of-markers">
          <div role="gridcell">
            <RoadmapTableColumnResizeProvider focusable>
              <RoadmapTableHeaderDragSash id="RoadmapTableHeaderDragSash" />
            </RoadmapTableColumnResizeProvider>
          </div>
        </div>
      ) : (
        <div id="end-of-markers" />
      )}
    </div>
  )
})

const STEPS_BY_ZOOM_LEVEL: {
  [zoomLevel in RoadmapZoomLevel]: number
} = {
  [RoadmapZoomLevel.Month]: 1,
  [RoadmapZoomLevel.Quarter]: 7,
  [RoadmapZoomLevel.Year]: 14,
}
/**
 * Days rendered depends on the Zoom level:
 * - Month: render every day.
 * - Quarter and Year: render only the first day of each week.
 *     Monday is the start of the work week, but eventually we may want to support a user-configurable start of week.
 */
const RoadmapDays = memo(function RoadmapDays() {
  const {columnWidth, getX, timeRangeEnd, timeRangeStart, today} = useRoadmapView()
  const zoomLevel = useRoadmapZoomLevel()

  const days = useMemo(() => {
    const dayTime = 24 * 60 * 60 * 1000
    const startDayUtc = timeRangeStart.getUTCDay() // 0-6, Sunday is 0
    const dayStep = STEPS_BY_ZOOM_LEVEL[zoomLevel]
    const firstDay = addDays(timeRangeStart, (dayStep - startDayUtc + 1) % dayStep) // first day of the week should be Monday, so add 1
    const totalDays = Math.floor(differenceInDays(timeRangeEnd, firstDay) / dayStep)
    const startTime = firstDay.getTime()

    return Array.from({length: totalDays}, (_, i) => {
      const date = new Date(startTime + i * dayStep * dayTime)
      const isToday = date.getTime() === today.getTime()
      const isoDateString = `${date.getUTCFullYear()}-${(date.getUTCMonth() + 1).toString().padStart(2, '0')}-${date
        .getUTCDate()
        .toString()
        .padStart(2, '0')}`

      return (
        <time
          key={isoDateString}
          // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
          role="columnheader"
          style={{
            left: getX(date),
            width: columnWidth,
          }}
          data-index={i}
          dateTime={isoDateString}
          aria-current={isToday ? 'date' : undefined}
          className={clsx(styles.day, isToday && styles.today)}
        >
          {date.getUTCDate()}
        </time>
      )
    })
  }, [columnWidth, getX, timeRangeEnd, timeRangeStart, today, zoomLevel])

  return (
    <div role="row" className={styles.timeRow}>
      {days}
    </div>
  )
})

const RoadmapMonths = memo(function RoadmapMonths() {
  const {timeRangeEnd, timeRangeStart, getX} = useRoadmapView()
  const zoomLevel = useRoadmapZoomLevel()
  const timeRangeStartMonthLocal = startOfMonth(timeRangeStart)
  const firstFullMonthUtc = startOfDayUtc(addMonths(timeRangeStartMonthLocal, 1))
  const spaceOnLeft = getX(firstFullMonthUtc) - MONTH_LEFT_PADDING

  const totalMonths = useMemo(
    () => differenceInMonths(timeRangeEnd, timeRangeStart) + 1,
    [timeRangeEnd, timeRangeStart],
  )

  const isYearZoomLevel = zoomLevel === RoadmapZoomLevel.Year

  const months = useMemo(() => {
    return Array.from({length: totalMonths}, (_, i) => {
      const dateFormat = isYearZoomLevel ? 'MMM yyyy' : 'MMMM yyyy'
      const firstOfMonthLocal = addMonths(timeRangeStartMonthLocal, i)
      const firstOfMonthUtc = startOfDayUtc(firstOfMonthLocal)
      const nextFirstOfMonthUtc = startOfDayUtc(addMonths(firstOfMonthLocal, 1))

      let width = 0
      let left = PAGE_PADDING - MONTH_LEFT_PADDING
      let paddingLeft = MONTH_LEFT_PADDING

      if (i === 0) {
        // First month: The first month requires special width/styling for partial month days and page padding
        left = 0
        if (spaceOnLeft <= 0) {
          paddingLeft = 0
          width = 0
        } else if (spaceOnLeft < PAGE_PADDING) {
          paddingLeft = spaceOnLeft
          width = spaceOnLeft
        } else {
          paddingLeft = PAGE_PADDING
          width = spaceOnLeft
        }
      } else if (i === 1 && spaceOnLeft < 0) {
        // Second month: The left-side padding of the second month (first full month) may need to be adjusted for available space on the left.
        if (firstOfMonthUtc.getTime() === timeRangeStart.getTime()) {
          width = getX(nextFirstOfMonthUtc) - getX(firstOfMonthUtc) - MONTH_LEFT_PADDING
        } else {
          const paddingToFitSpace = MONTH_LEFT_PADDING + spaceOnLeft
          paddingLeft = paddingToFitSpace
          left = -paddingToFitSpace
          width = getX(nextFirstOfMonthUtc) - getX(firstOfMonthUtc) - MONTH_LEFT_PADDING + paddingToFitSpace
        }
      } else if (i === totalMonths - 1) {
        // Last month: The last month requires special width/styling for partial month days and available space on the right.
        const spaceOnRight = getX(timeRangeEnd) - getX(firstOfMonthUtc) + MONTH_LEFT_PADDING - PAGE_PADDING_RIGHT
        if (spaceOnRight <= 0) {
          paddingLeft = 0
          width = 0
        } else if (spaceOnRight < MONTH_LEFT_PADDING) {
          paddingLeft = spaceOnRight
          width = spaceOnRight
        } else {
          width = spaceOnRight
        }
      } else {
        // All other month headers are full width
        width = getX(nextFirstOfMonthUtc) - getX(firstOfMonthUtc)
      }

      return (
        <div
          role="columnheader"
          key={firstOfMonthLocal.getTime()}
          style={{
            width: `${width}px`,
            left: `${left}px`,
            paddingLeft: `${paddingLeft}px`,
          }}
          className={styles.monthHeaderCell}
        >
          {formatDate(firstOfMonthLocal, dateFormat)}
        </div>
      )
    })
  }, [totalMonths, isYearZoomLevel, timeRangeStartMonthLocal, spaceOnLeft, timeRangeStart, getX, timeRangeEnd])

  return (
    <div role="row" className={styles.timeRow}>
      {months}
      <RoadmapControlsCaulk />
    </div>
  )
})

/** Seals the gap between the absolute positioned roadmap contols and the browser edge/scrollbar so we don't see month header text */
const RoadmapControlsCaulk = () => {
  return <div className={styles.controlsCaulk} />
}

const RoadmapMarkerHeaders = memo(function RoadmapMarkerHeaders() {
  const {getX} = useRoadmapView()
  const {markersInRange} = useRoadmapMarkersInRange()
  const iterationCount = useRoadmapIterationMarkersSize()
  const milestonesCount = useRoadmapMilestoneMarkersSize()

  // Keeping these as separate arrays for the time being, since iteration markers are sticky

  const {milestoneMarkers, iterationMarkers} = useMemo(
    () =>
      markersInRange.reduce<{
        milestoneMarkers: Array<MilestoneMarker>
        iterationMarkers: Array<IterationMarker>
      }>(
        (acc, marker) => {
          if (marker.type === RoadmapMarkerType.Iteration || marker.type === RoadmapMarkerType.IterationBreak) {
            acc.iterationMarkers.push(marker)
          } else if (marker.type === RoadmapMarkerType.Milestone) {
            acc.milestoneMarkers.push(marker)
          }

          return acc
        },
        {iterationMarkers: [], milestoneMarkers: []},
      ),
    [markersInRange],
  )

  const firstIterationMarker = iterationMarkers[0]
  const firstIterationLeftOffset = firstIterationMarker ? getX(new Date(firstIterationMarker.date)) : 0

  return markersInRange.length > 0 || milestonesCount > 0 ? (
    <div role="row" className={styles.markersRow}>
      <div
        role="presentation"
        style={{
          width: `${firstIterationLeftOffset - MARKER_LEFT_PADDING}px`,
        }}
        className={styles.defaultIterationHeader}
        {...testIdProps(`roadmap-iteration-marker-header-spacer`)}
      />

      {iterationMarkers.map((marker, index) => {
        return <RoadmapIterationHeader key={marker.id} marker={marker} nextMarker={iterationMarkers[index + 1]} />
      })}
      {iterationCount > 0 && (
        <div
          role="presentation"
          className={clsx(styles.defaultIterationHeader, styles.iterationHeaderFill)}
          {...testIdProps(`roadmap-iteration-marker-header-fill`)}
        />
      )}
      {milestoneMarkers.map(marker => {
        return <RoadmapMilestoneHeader key={marker.id} marker={marker} />
      })}
    </div>
  ) : null
})

function RoadmapMilestoneHeader({marker}: {marker: MilestoneMarker}) {
  const {getX} = useRoadmapView()

  const dueDateAtMidday = useMemo(() => addHours(new Date(marker.date), 12), [marker.date])
  const leftOffset = getX(dueDateAtMidday)

  const ref = useRef<HTMLDivElement>(null)
  const [contentProps, tooltip] = usePortalTooltip({
    contentRef: ref,
    'aria-label': getTooltipText({
      start: dueDateAtMidday,
      end: dueDateAtMidday,
    }),
  })
  return (
    <div
      ref={ref}
      role="columnheader"
      style={{
        left: leftOffset,
      }}
      className={clsx(styles.defaultIterationHeader, styles.milestoneHeader)}
      {...contentProps}
      {...testIdProps(`roadmap-milestone-marker-header`)}
    >
      {(marker.options.length || 0) > 1 ? (
        <RoadmapMilestoneMarkerActionMenu marker={marker} />
      ) : (
        <RoadmapMilestoneMarkerHeaderLink marker={marker} />
      )}
      {tooltip}
    </div>
  )
}

function RoadmapIterationHeader({marker, nextMarker}: {marker: IterationMarker; nextMarker?: IterationMarker}) {
  const {getX, timeRangeEnd} = useRoadmapView()
  const isCurrent = isCurrentIterationByMarker(marker)
  const startDate = new Date(marker.date)
  const endDate = nextMarker ? new Date(nextMarker.date) : addDays(startDate, marker.duration || 0)
  const leftOffset = getX(startDate)
  // if the iteration ends after the time range, we need to use the time range end date
  const rightOffset = getX(timeRangeEnd < endDate ? timeRangeEnd : endDate)
  const width = rightOffset - leftOffset
  const ref = useRef<HTMLDivElement>(null)
  const [contentProps, tooltip] = usePortalTooltip({
    contentRef: ref,
    'aria-label': getIterationOptionsTooltipText(marker.options),
    anchorOffset: 6,
  })
  return (
    <div
      ref={ref}
      role="columnheader"
      style={{
        width: `${width}px`,
      }}
      className={clsx(styles.defaultIterationHeader, isCurrent && styles.currentIterationHeader)}
      {...testIdProps(`roadmap-iteration-marker-header-${marker.id}`)}
    >
      <HoverOverPortalContent
        ref={ref}
        className={clsx(styles.defaultIterationHeader, isCurrent && styles.currentIterationHeader)}
        {...contentProps}
      >
        {marker.options.length === 1 ? (
          <>{marker.title}</>
        ) : (
          <ActionMenu>
            <ActionMenu.Anchor>
              <Button title={marker.title} aria-label={marker.title} className={styles.milestoneHeaderButton}>
                {marker.title}
                {marker.date && (
                  <span className="sr-only">
                    (Starting on <time dateTime={marker.date}>{formatDateUtc(new Date(marker.date))}</time>)
                  </span>
                )}
              </Button>
            </ActionMenu.Anchor>
            <ActionMenu.Overlay className={styles.milestoneMenuOverlay}>
              <ActionList {...testIdProps(`roadmap-iteration-marker-selector`)}>
                {marker.options.map(option => {
                  const {startDate: optionStartDate, endDate: optionEndDate} = intervalDateRange({
                    startDate: option.startDate,
                    duration: option.duration,
                  })
                  return (
                    <ActionList.Item
                      key={option.id}
                      active={false}
                      selected={false}
                      className={styles.milestoneMenuOption}
                    >
                      {option.title}
                      <ActionList.Description truncate>
                        {getTooltipText({
                          start: optionStartDate,
                          end: optionEndDate,
                        })}
                      </ActionList.Description>
                    </ActionList.Item>
                  )
                })}
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        )}
      </HoverOverPortalContent>
      {tooltip}
    </div>
  )
}

function RoadmapMilestoneMarkerHeaderLink({marker}: {marker: MilestoneMarker}) {
  const firstOption = marker.options[0]
  const url: string | undefined = firstOption?.url

  return (
    <Link
      target="_blank"
      rel="noopener noreferrer"
      href={url}
      title={marker.description}
      className={styles.milestoneHeaderLink}
    >
      {marker.title}
      {marker.date && (
        <span className="sr-only">
          (Due on <time dateTime={marker.date}>{formatDateUtc(new Date(marker.date))}</time>)
        </span>
      )}
    </Link>
  )
}

function RoadmapMilestoneMarkerActionMenu({marker}: {marker: MilestoneMarker}) {
  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <Button
          as="button"
          title={marker.description}
          aria-label={marker.description}
          className={styles.milestoneHeaderButton}
        >
          {marker.options.length} milestones
          {marker.date && (
            <span className="sr-only">
              (Due on <time dateTime={marker.date}>{formatDateUtc(new Date(marker.date))}</time>)
            </span>
          )}
        </Button>
      </ActionMenu.Anchor>
      <ActionMenu.Overlay className={styles.milestoneMenuOverlay}>
        <ActionList {...testIdProps(`roadmap-milestone-marker-selector`)}>
          {marker.options.map(option => {
            return (
              <ActionList.LinkItem href={option.url} target="_blank" rel="noopener noreferrer" key={option.id}>
                {option.title}
                <ActionList.Description truncate>{option.repoNameWithOwner}</ActionList.Description>
              </ActionList.LinkItem>
            )
          })}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

/**
 * This component renders content, and also renders a portal with the same content
 * that displays when the user hovers over the content, avoiding z-index issues related to this
 * element being in a different stacking context.
 */
function HoverOverPortalContent({
  ref: forwardedRef,
  children,
  className,
  ...rest
}: ComponentPropsWithoutRef<'div'> & {ref?: React.Ref<HTMLElement>}) {
  const contentRef = useRef<HTMLDivElement>(null)
  useImperativeHandle<HTMLElement | null, HTMLElement | null>(forwardedRef, () => contentRef.current)
  const [showOverlay, setShowOverlay] = useState(false)
  return (
    <div
      ref={contentRef}
      {...rest}
      onPointerEnter={e => {
        setShowOverlay(true)
        rest.onPointerEnter?.(e)
      }}
      onPointerLeave={e => {
        setShowOverlay(false)
        rest.onPointerLeave?.(e)
      }}
      className={styles.hoverableContainer}
    >
      {children}
      {showOverlay ? (
        <OverlayingContent contentRef={contentRef} className={className}>
          {children}
        </OverlayingContent>
      ) : null}
    </div>
  )
}

function OverlayingContent({
  contentRef,
  children,
  className,
}: {
  contentRef: React.RefObject<HTMLDivElement | null>
  children: React.ReactNode
  className?: string
}) {
  const floatingRef = useRef<HTMLDivElement>(null)
  const {left, top} = usePositionRelativeToContentRefBasedOnRoadmapScroll(floatingRef, contentRef)

  return (
    <Portal>
      <div
        role="presentation"
        aria-hidden
        style={{
          left,
          top,
        }}
        ref={floatingRef}
        className={clsx(styles.overlayContent, className)}
      >
        {children}
      </div>
    </Portal>
  )
}

const usePositionRelativeToContentRefBasedOnRoadmapScroll = (
  floatingElementRef: React.RefObject<HTMLElement | null>,
  contentRef: React.RefObject<HTMLElement | null>,
) => {
  const {roadmapRef} = useRoadmapNavigation()
  const currentAnchorPosition = useRef({
    top: 0,
    left: 0,
  })

  return useSyncExternalStore(
    useCallback(
      notify => {
        roadmapRef.current?.addEventListener('scroll', notify)
        return () => {
          roadmapRef.current?.removeEventListener('scroll', notify)
        }
      },
      [roadmapRef],
    ),
    useCallback(() => {
      if (!floatingElementRef.current || !contentRef.current) return currentAnchorPosition.current
      const {left, top} = getAnchoredPosition(floatingElementRef.current, contentRef.current, {
        side: 'inside-top',
        align: 'start',
        alignmentOffset: 0,
        anchorOffset: 0,
      })
      /**
       * Only update the object if the position has changed. This is to prevent excessive re-renders
       */
      if (currentAnchorPosition.current.left !== left || currentAnchorPosition.current.top !== top) {
        currentAnchorPosition.current = {
          left,
          top,
        }
      }

      return currentAnchorPosition.current
    }, [contentRef, floatingElementRef]),
  )
}
